// Блог — серверные функции (17.10.2026).
//
// Чтение наружу — только status = 'published' (фильтр в запросе, сервис-роль;
// RLS таблицы разрешает анониму то же самое). До наката 20261101120000
// таблицы нет — блог пустой, без ошибок (isMissingTable).
//
// Запись — только /admin, вкладка «Блог»: requireSupabaseAuth + роль admin.
// published_at ставится при первой публикации и дальше не меняется.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { COACHES } from "@/data/coaches";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import {
  BLOG_DESCRIPTION_MAX,
  BLOG_LANGS,
  BLOG_PAGE_SIZE,
  BLOG_SLUG_RE,
  BLOG_TITLE_MAX,
  readMinutes,
  type BlogCard,
  type BlogLang,
  type BlogPost,
  type BlogTranslation,
} from "@/lib/blog";
import { forbidden } from "@/lib/edu-forbidden.server";
import { decodePhoto } from "@/lib/map-photo";

type LooseDb = {
  from: (table: string) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
  storage: { from: (b: string) => any }; // eslint-disable-line @typescript-eslint/no-explicit-any
};
const db = supabaseAdmin as unknown as LooseDb;

export const BLOG_COVER_BUCKET = "blog-covers";
const CARD_COLS = "id, lang, slug, title, description, cover_url, author_coach_id, published_at, updated_at";
const POST_COLS = `${CARD_COLS}, body_md, translation_group`;

/** Таблицы ещё нет (миграция не накатана) — блог пустой, а не ошибка. */
export const isMissingTable = (e: { code?: string | null } | null | undefined) => !!e && ["42P01", "PGRST205"].includes(e.code ?? "");

const LangSchema = z.enum(BLOG_LANGS as [BlogLang, ...BlogLang[]]);

export const listBlogPosts = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ lang: LangSchema, page: z.number().int().min(1).max(1000) }).parse(input))
  .handler(async ({ data }): Promise<{ cards: BlogCard[]; total: number }> => {
    const from = (data.page - 1) * BLOG_PAGE_SIZE;
    const { data: rows, error, count } = await db
      .from("blog_posts")
      // body_md — только чтобы посчитать время чтения; наружу тело не уходит.
      .select(`${CARD_COLS}, body_md`, { count: "exact" })
      .eq("status", "published")
      .eq("lang", data.lang)
      .order("published_at", { ascending: false })
      .range(from, from + BLOG_PAGE_SIZE - 1);
    if (isMissingTable(error)) return { cards: [], total: 0 };
    if (error) throw new Error(error.message);
    const cards = ((rows ?? []) as Array<Omit<BlogCard, "read_minutes"> & { body_md: string }>).map(({ body_md, ...card }) => ({
      ...card,
      read_minutes: readMinutes(body_md ?? ""),
    }));
    return { cards, total: count ?? 0 };
  });

export const getBlogPost = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ lang: LangSchema, slug: z.string().min(1).max(100) }).parse(input))
  .handler(async ({ data }): Promise<{ post: BlogPost | null; translations: BlogTranslation[] }> => {
    if (!BLOG_SLUG_RE.test(data.slug)) return { post: null, translations: [] };
    const one = (cols: string) => db.from("blog_posts").select(cols).eq("status", "published").eq("lang", data.lang).eq("slug", data.slug).maybeSingle();
    // seo_title — с 20261115120000; до наката колонки статья читается без него.
    let { data: row, error } = await one(`${POST_COLS}, seo_title`);
    if (error && (error.code === "42703" || error.code === "PGRST204" || /seo_title/.test(error.message ?? ""))) ({ data: row, error } = await one(POST_COLS));
    if (isMissingTable(error)) return { post: null, translations: [] };
    if (error) throw new Error(error.message);
    if (!row) return { post: null, translations: [] };
    const post = row as BlogPost;
    const { data: tr } = await db
      .from("blog_posts")
      .select("lang, slug")
      .eq("status", "published")
      .eq("translation_group", post.translation_group)
      .neq("id", post.id);
    return { post, translations: (tr ?? []) as BlogTranslation[] };
  });

/** Статьи коучей на языке страницы /coaches: id коуча → [{slug, title}]. Сбой — пусто (страница коучей важнее). */
export const listCoachPosts = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ lang: LangSchema }).parse(input))
  .handler(async ({ data }): Promise<Record<string, { slug: string; title: string }[]>> => {
    const { data: rows, error } = await db
      .from("blog_posts")
      .select("slug, title, author_coach_id")
      .eq("status", "published")
      .eq("lang", data.lang)
      .not("author_coach_id", "is", null)
      .order("published_at", { ascending: false })
      .limit(200);
    if (error) {
      if (!isMissingTable(error)) console.error("[blog] статьи коучей", error.message);
      return {};
    }
    const out: Record<string, { slug: string; title: string }[]> = {};
    for (const r of (rows ?? []) as { slug: string; title: string; author_coach_id: string }[]) (out[r.author_coach_id] ??= []).push({ slug: r.slug, title: r.title });
    return out;
  });

// ─── /admin ───────────────────────────────────────────────────────────

async function assertAdmin(context: { supabase: unknown; userId: string }) {
  const sb = context.supabase as LooseDb;
  const { data: role } = await sb.from("user_roles").select("role").eq("user_id", context.userId).eq("role", "admin").maybeSingle();
  if (!role) throw forbidden("Forbidden: admin role required");
}

export type AdminBlogPost = BlogPost & { status: "draft" | "published"; created_at: string };

export const adminListBlogPosts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ posts: AdminBlogPost[]; ready: boolean }> => {
    await assertAdmin(context as { supabase: unknown; userId: string });
    const { data, error } = await db.from("blog_posts").select(`${POST_COLS}, status, created_at`).order("updated_at", { ascending: false }).limit(500);
    if (isMissingTable(error)) return { posts: [], ready: false };
    if (error) throw new Error(error.message);
    return { posts: (data ?? []) as AdminBlogPost[], ready: true };
  });

const SaveInput = z.object({
  id: z.string().uuid().nullable(),
  lang: LangSchema,
  title: z.string().trim().min(1).max(BLOG_TITLE_MAX),
  slug: z.string().trim().min(1).max(100).regex(BLOG_SLUG_RE),
  description: z.string().trim().max(BLOG_DESCRIPTION_MAX),
  body_md: z.string().max(100_000),
  cover_url: z.string().trim().url().max(500).nullable(),
  author_coach_id: z.string().nullable().refine((v) => v === null || COACHES.some((c) => c.id === v), "author"),
  /** «Связать с переводом»: id статьи, чью группу переводов взять. null — своя группа. */
  link_to_id: z.string().uuid().nullable(),
  publish: z.boolean(),
});

export const adminSaveBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => SaveInput.parse(input))
  .handler(async ({ data, context }): Promise<{ ok: true; id: string } | { ok: false; reason: "slug_taken" | "lang_taken" | "not_found" }> => {
    await assertAdmin(context as { supabase: unknown; userId: string });
    const existing = data.id
      ? ((await db.from("blog_posts").select("id, published_at, translation_group").eq("id", data.id).maybeSingle()).data as { id: string; published_at: string | null; translation_group: string } | null)
      : null;
    if (data.id && !existing) return { ok: false, reason: "not_found" };

    let group = existing?.translation_group ?? null;
    if (data.link_to_id) {
      const { data: other } = await db.from("blog_posts").select("translation_group").eq("id", data.link_to_id).maybeSingle();
      if (!other) return { ok: false, reason: "not_found" };
      group = (other as { translation_group: string }).translation_group;
    }
    // В группе переводов — не больше одной статьи на язык.
    if (group) {
      const { data: same } = await db.from("blog_posts").select("id").eq("translation_group", group).eq("lang", data.lang);
      if (((same ?? []) as { id: string }[]).some((r) => r.id !== data.id)) return { ok: false, reason: "lang_taken" };
    }

    const row: Record<string, unknown> = {
      lang: data.lang,
      title: data.title,
      slug: data.slug,
      description: data.description,
      body_md: data.body_md,
      cover_url: data.cover_url,
      author_coach_id: data.author_coach_id,
      status: data.publish ? "published" : "draft",
      published_at: data.publish ? (existing?.published_at ?? new Date().toISOString()) : (existing?.published_at ?? null),
      ...(group ? { translation_group: group } : {}),
    };
    const res = existing
      ? await db.from("blog_posts").update(row).eq("id", existing.id).select("id").single()
      : await db.from("blog_posts").insert(row).select("id").single();
    if (res.error?.code === "23505") return { ok: false, reason: "slug_taken" };
    if (res.error) throw new Error(res.error.message);
    return { ok: true, id: (res.data as { id: string }).id };
  });

/** Обложка в Storage (публичный бакет blog-covers): base64 → проверка формата и размера → ссылка. */
export const adminUploadBlogCover = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ b64: z.string().min(1) }).parse(input))
  .handler(async ({ data, context }): Promise<{ ok: true; url: string } | { ok: false; reason: string }> => {
    await assertAdmin(context as { supabase: unknown; userId: string });
    const photo = decodePhoto(data.b64, true);
    if (!photo || !photo.ok) return { ok: false, reason: photo && !photo.ok ? photo.reason : "photo_type" };
    const key = `${crypto.randomUUID()}.${photo.ext}`;
    const bucket = db.storage.from(BLOG_COVER_BUCKET);
    const up = await bucket.upload(key, photo.bytes, { contentType: photo.mime, upsert: false });
    if (up.error) return { ok: false, reason: up.error.message };
    return { ok: true, url: bucket.getPublicUrl(key).data.publicUrl as string };
  });
